import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { useArticles } from '../contexts/ArticleContext';
import ArticleCard from '../components/molecules/ArticleCard';
import SEO from '../components/SEO';

const PageWrapper = styled.div`
  background-color: ${theme.colors.primary};
  color: ${theme.colors.white};
  min-height: 100vh;
  padding-bottom: ${theme.space.xl};
`;

const ArticlesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${theme.space.xl} ${theme.space.md};
`;

const ArticlesHeader = styled.div`
  margin-bottom: ${theme.space.xl};
  text-align: center;
  margin-top: ${theme.space.xl};
`;

const ArticlesTitle = styled.h1`
  font-size: ${theme.fontSizes['4xl']};
  margin-bottom: ${theme.space.sm};
  color: ${theme.colors.white};
`;

const ArticlesDescription = styled.p`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.white};
`;

const BreadcrumbNav = styled.nav`
  margin-bottom: ${theme.space.lg};
  
  a {
    color: ${theme.colors.white};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  span {
    margin: 0 ${theme.space.xs};
    color: ${theme.colors.white};
  }
`;

const ArticlesGrid = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  gap: ${theme.space.md};
`;

const NoArticlesMessage = styled.p`
  text-align: center;
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.white};
  padding: ${theme.space.xl} 0;
`;

const LoadingMessage = styled.div`
  text-align: center;
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.white};
  padding: ${theme.space.xl} 0;
`;

const ErrorMessage = styled.div`
  text-align: center;
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.error};
  padding: ${theme.space.xl} 0;
  border: 1px solid ${theme.colors.error};
  border-radius: ${theme.radii.md};
  margin: ${theme.space.xl} 0;
  background-color: rgba(255, 255, 255, 0.9);
`;

const FilterContainer = styled.div`
  margin-bottom: ${theme.space.lg};
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${theme.space.sm};
`;

const FilterButton = styled.button<{ active: boolean }>`
  padding: ${theme.space.sm} ${theme.space.md};
  border-radius: ${theme.radii.md};
  border: none;
  background: ${props => props.active ? '#D67616' : 'rgba(255, 255, 255, 0.9)'};
  color: ${props => props.active ? '#ffffff' : '#1976d2'};
  font-size: ${theme.fontSizes.md};
  cursor: pointer;
  transition: ${theme.transitions.default};
  
  &:hover {
    background: ${props => props.active ? '#e55a00' : 'rgba(255, 255, 255, 0.7)'};
  }
`;

const CountBadge = styled.span<{ active?: boolean }>`
  display: inline-block;
  margin-left: ${theme.space.xs};
  font-size: ${theme.fontSizes.sm};
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.9)' : '#1976d2'};
  color: ${props => props.active ? '#D67616' : 'white'};
  padding: 2px 6px;
  border-radius: ${theme.radii.full};
`;

const HomeButton = styled(Link)`
  background-color: #D67616;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 30px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s;
  display: block;
  margin: 30px auto 0;
  text-align: center;
  width: fit-content;
  text-decoration: none;
  
  &:hover {
    background-color: #e55a00;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: ${theme.space.md};
  margin-top: ${theme.space.xl};
`;

const PaginationButton = styled.button<{ active?: boolean; disabled?: boolean }>`
  padding: ${theme.space.sm} ${theme.space.md};
  border-radius: ${theme.radii.md};
  border: none;
  background: ${props => props.active ? '#D67616' : 'rgba(255, 255, 255, 0.9)'};
  color: ${props => props.active ? '#ffffff' : props.disabled ? '#888888' : '#1976d2'};
  font-size: ${theme.fontSizes.md};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.6 : 1};
  transition: ${theme.transitions.default};
  
  &:hover {
    background: ${props => props.disabled ? 'rgba(255, 255, 255, 0.9)' : props.active ? '#e55a00' : 'rgba(255, 255, 255, 0.7)'};
  }
`;

function ArticleListPage() {
  const { articles, loading, error } = useArticles();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const articlesPerPage = 10;
  
  // カテゴリーの一覧とそれぞれの記事数を取得
  const categories = useMemo(() => {
    const counts: { [key: string]: number } = { 'all': articles.length };
    
    articles.forEach(article => {
      const cat = article.category || 'その他';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    
    return Object.entries(counts).sort((a, b) => {
      if (a[0] === 'all') return -1;
      if (b[0] === 'all') return 1;
      return a[0].localeCompare(b[0]);
    });
  }, [articles]);
  
  // カテゴリーと日付でフィルタリング・ソート
  const filteredArticles = useMemo(() => {
    return [...articles]
      .filter(article => selectedCategory === 'all' || article.category === selectedCategory)
      .sort((a, b) => {
        // 日付が正しい形式の場合は日付でソート
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        
        // 日付の降順でソート（新しい順）
        if (!isNaN(dateA) && !isNaN(dateB)) {
          return dateB - dateA;
        }
        
        // 日付が不正な場合はタイトルでソート
        return a.title.localeCompare(b.title);
      });
  }, [articles, selectedCategory]);
  
  // ページネーション - 表示する記事を計算
  const currentArticles = useMemo(() => {
    const indexOfLastArticle = currentPage * articlesPerPage;
    const indexOfFirstArticle = indexOfLastArticle - articlesPerPage;
    return filteredArticles.slice(indexOfFirstArticle, indexOfLastArticle);
  }, [filteredArticles, currentPage, articlesPerPage]);
  
  // 全ページ数を計算
  const totalPages = useMemo(() => {
    return Math.ceil(filteredArticles.length / articlesPerPage);
  }, [filteredArticles, articlesPerPage]);
  
  // ページネーションのページ番号を生成
  const pageNumbers = useMemo(() => {
    const pages = [];
    
    // 最大5ページのページネーションを表示
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + 4);
    
    // 末尾のページに達した場合、開始ページを調整
    if (endPage - startPage < 4 && startPage > 1) {
      startPage = Math.max(1, endPage - 4);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  }, [currentPage, totalPages]);
  
  // カテゴリー変更時にページをリセット
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);
  
  // ページ変更ハンドラー
  const handlePageChange = (pageNumber: number) => {
    // ウィンドウをトップにスクロール
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCurrentPage(pageNumber);
  };
  
  return (
    <PageWrapper>
      <SEO 
        title="記事一覧"
        description="アラヨットラベル - すべての旅行記事一覧です。国内・海外の旅行情報、観光スポット、グルメ情報を提供します。"
        keywords="旅行, 記事一覧, 観光, アラヨットラベル"
        canonical="/articles"
      />
      <ArticlesContainer>
        <BreadcrumbNav>
          <Link to="/">ホーム</Link>
          <span>/</span>
          <span>記事一覧</span>
        </BreadcrumbNav>
        
        <ArticlesHeader>
          <ArticlesTitle>記事一覧</ArticlesTitle>
          <ArticlesDescription>すべての記事をご覧いただけます</ArticlesDescription>
        </ArticlesHeader>
        
        {loading ? (
          <LoadingMessage>記事を読み込み中...</LoadingMessage>
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : articles.length > 0 ? (
          <>
            <FilterContainer>
              {categories.map(([category, count]) => (
                <FilterButton 
                  key={category}
                  active={selectedCategory === category}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category === 'all' ? 'すべて' : category}
                  <CountBadge active={selectedCategory === category}>{count}</CountBadge>
                </FilterButton>
              ))}
            </FilterContainer>
            
            {currentArticles.length > 0 ? (
              <>
                <ArticlesGrid>
                  {currentArticles.map(article => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </ArticlesGrid>
                
                {/* ページネーション */}
                {totalPages > 1 && (
                  <PaginationContainer>
                    <PaginationButton 
                      onClick={() => handlePageChange(1)} 
                      disabled={currentPage === 1}
                    >
                      &laquo;
                    </PaginationButton>
                    
                    <PaginationButton 
                      onClick={() => handlePageChange(currentPage - 1)} 
                      disabled={currentPage === 1}
                    >
                      &lt;
                    </PaginationButton>
                    
                    {pageNumbers.map(number => (
                      <PaginationButton
                        key={number}
                        active={currentPage === number}
                        onClick={() => handlePageChange(number)}
                      >
                        {number}
                      </PaginationButton>
                    ))}
                    
                    <PaginationButton 
                      onClick={() => handlePageChange(currentPage + 1)} 
                      disabled={currentPage === totalPages}
                    >
                      &gt;
                    </PaginationButton>
                    
                    <PaginationButton 
                      onClick={() => handlePageChange(totalPages)} 
                      disabled={currentPage === totalPages}
                    >
                      &raquo;
                    </PaginationButton>
                  </PaginationContainer>
                )}
              </>
            ) : (
              <NoArticlesMessage>
                選択されたカテゴリーに記事がありません。
              </NoArticlesMessage>
            )}
            
            <HomeButton to="/" onClick={() => window.scrollTo(0, 0)}>ホームへ戻る</HomeButton>
          </>
        ) : (
          <NoArticlesMessage>記事がまだありません。</NoArticlesMessage>
        )}
      </ArticlesContainer>
    </PageWrapper>
  );
}

export default ArticleListPage; 